import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useFormValidation } from '../../hooks/useFormValidation';
import apiClient from '../../services/api';
import FormButton from '../../components/common/stories/FormButton';

// US States
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

// Canadian Provinces
const CANADA_PROVINCES = [
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
  'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
  'Quebec', 'Saskatchewan', 'Yukon'
];

/**
 * OrganizationSetupForm - Create organization and first store location
 * Shown after user redeems an admin license token
 */
const OrganizationSetupForm = ({ userCountry, maxLocations, token: adminToken, onSetupComplete }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Organization data
  const [orgData, setOrgData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
  });

  // Store location data
  const [locationData, setLocationData] = useState({
    storeName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    storePhoneNumber: '',
    storeEmail: '',
    storeWebsite: '',
  });

  const country = userCountry || 'Canada';
  const stateProvinceOptions = country === 'USA' ? US_STATES : CANADA_PROVINCES;
  const stateLabel = country === 'USA' ? 'State' : 'Province';
  const postalLabel = country === 'USA' ? 'ZIP Code' : 'Postal Code';

  // Validation rules
  const orgValidationRules = {
    name: (value) => (!value ? 'Organization name is required' : ''),
    email: (value) => {
      if (!value) return 'Email is required';
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !emailPattern.test(value) ? 'Please enter a valid email' : '';
    },
    phone: (value) => {
      if (!value) return 'Phone number is required';
      const phonePattern = /^\d{3}-\d{3}-\d{4}$/;
      return !phonePattern.test(value) ? 'Phone must be in format xxx-xxx-xxxx' : '';
    },
  };

  const locationValidationRules = {
    storeName: (value) => (!value ? 'Store name is required' : ''),
    addressLine1: (value) => (!value ? 'Address is required' : ''),
    city: (value) => (!value ? 'City is required' : ''),
    state: (value) => (!value ? `${stateLabel} is required` : ''),
    postalCode: (value) => {
      if (!value) return `${postalLabel} is required`;
      if (country === 'USA') {
        const usZip = /^\d{5}(-\d{4})?$/;
        return !usZip.test(value) ? 'Invalid ZIP code format' : '';
      } else {
        const caPostal = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
        return !caPostal.test(value) ? 'Invalid postal code format (e.g., A1A 1A1)' : '';
      }
    },
    storePhoneNumber: (value) => {
      if (!value) return 'Store phone is required';
      const phonePattern = /^\d{3}-\d{3}-\d{4}$/;
      return !phonePattern.test(value) ? 'Phone must be in format xxx-xxx-xxxx' : '';
    },
    storeEmail: (value) => {
      if (!value) return 'Store email is required';
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !emailPattern.test(value) ? 'Please enter a valid email' : '';
    },
  };

  const { errors, validateField, validateFields, setErrors } = useFormValidation();

  const handleOrgChange = (e) => {
    const { name, value } = e.target;
    setOrgData({ ...orgData, [name]: value });
    validateField(name, value, orgValidationRules[name]);
    setSubmitError('');
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setLocationData({ ...locationData, [name]: value });
    validateField(name, value, locationValidationRules[name]);
    setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate organization fields
    const orgFieldsToValidate = Object.keys(orgValidationRules).reduce((acc, key) => {
      acc[key] = { value: orgData[key], validation: orgValidationRules[key] };
      return acc;
    }, {});

    // Validate location fields
    const locationFieldsToValidate = Object.keys(locationValidationRules).reduce((acc, key) => {
      acc[key] = { value: locationData[key], validation: locationValidationRules[key] };
      return acc;
    }, {});

    const allFieldsToValidate = { ...orgFieldsToValidate, ...locationFieldsToValidate };
    const isValid = validateFields(allFieldsToValidate);

    if (!isValid) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const authToken = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${authToken}` };

      // Step 1: Redeem admin token (grants admin role + creates organization)
      const redeemResponse = await apiClient.post(
        '/api/tokens/redeem',
        {
          token: adminToken,
          organization_name: orgData.name,
          organization_email: orgData.email,
          organization_phone: orgData.phone,
          organization_website: orgData.website || undefined,
        },
        { headers }
      );

      // API returns { organization: { id, name, ... } }
      const organizationId = redeemResponse.data.organization?.id || redeemResponse.data.organization_id;

      // Step 2: Create first store location
      const locationResponse = await apiClient.post(
        '/api/retailerlocations',
        {
          organization_id: organizationId,
          store_name: locationData.storeName,
          address_line1: locationData.addressLine1,
          address_line2: locationData.addressLine2 || undefined,
          city: locationData.city,
          state: locationData.state,
          postal_code: locationData.postalCode,
          country: country,
          phone: locationData.storePhoneNumber,
          email: locationData.storeEmail,
          website: locationData.storeWebsite || undefined,
        },
        { headers }
      );

      // API returns { message, result } where result is the created document
      const locationResult = locationResponse.data.result || locationResponse.data;
      const locationId = locationResult._id || locationResult.id;

      // Store first location in localStorage for selection
      localStorage.setItem('selectedLocation', locationId);
      localStorage.setItem('selectedOrganization', organizationId);

      // Notify parent of successful setup
      onSetupComplete(organizationId, locationId);
    } catch (error) {
      console.error('Organization setup error:', error);
      setSubmitError(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to create organization. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .org-setup-container {
          width: 100%;
          max-width: 700px;
          margin: 0 auto;
          padding: 0 20px;
          box-sizing: border-box;
        }

        .org-setup-section {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
        }

        .org-setup-section-title {
          font-size: 16px;
          font-weight: 600;
          color: #111827;
          margin: 0 0 6px 0;
        }

        .org-setup-section-subtitle {
          font-size: 13px;
          color: #6b7280;
          margin: 0 0 20px 0;
        }

        .org-setup-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        @media (max-width: 600px) {
          .org-setup-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }

        .org-setup-field {
          display: flex;
          flex-direction: column;
        }

        .org-setup-field.full-width {
          grid-column: 1 / -1;
        }

        .org-setup-field label {
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 6px;
        }

        .org-setup-field label .required {
          color: #ef4444;
          margin-left: 2px;
        }

        .org-setup-field input,
        .org-setup-field select {
          width: 100%;
          padding: 11px 14px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          color: #111827;
          background: #fff;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .org-setup-field input:focus,
        .org-setup-field select:focus {
          outline: none;
          border-color: #ff8264;
          box-shadow: 0 0 0 3px rgba(255, 130, 100, 0.15);
        }

        .org-setup-field select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 12px center;
          background-repeat: no-repeat;
          background-size: 16px;
          padding-right: 40px;
        }

        .org-setup-field .error-message {
          color: #ef4444;
          font-size: 12px;
          margin-top: 4px;
        }

        .org-setup-field .hint {
          color: #6b7280;
          font-size: 12px;
          margin-top: 4px;
        }

        .submit-error {
          background: #fef2f2;
          border: 1px solid #fee2e2;
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 20px;
          color: #991b1b;
          font-size: 14px;
        }

        .submit-row {
          display: flex;
          justify-content: center;
          margin-top: 8px;
        }

        .license-info {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .license-info svg {
          color: #16a34a;
          flex-shrink: 0;
        }

        .license-info-text {
          font-size: 13px;
          color: #166534;
        }
      `}</style>

      <div className="org-setup-container">
        {maxLocations && (
          <div className="license-info">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span className="license-info-text">
              Your license allows up to <strong>{maxLocations}</strong> store locations.
            </span>
          </div>
        )}

        {submitError && <div className="submit-error">{submitError}</div>}

        <form onSubmit={handleSubmit}>
          {/* Organization Section */}
          <div className="org-setup-section">
            <h3 className="org-setup-section-title">Organization Details</h3>
            <p className="org-setup-section-subtitle">
              Enter your company information. This will be visible to your team members.
            </p>

            <div className="org-setup-grid">
              <div className="org-setup-field full-width">
                <label htmlFor="name">
                  Organization Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={orgData.name}
                  onChange={handleOrgChange}
                  placeholder="e.g., Fresh Mart Groceries"
                  disabled={isSubmitting}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="org-setup-field">
                <label htmlFor="email">
                  Organization Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={orgData.email}
                  onChange={handleOrgChange}
                  placeholder="contact@company.com"
                  disabled={isSubmitting}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="org-setup-field">
                <label htmlFor="phone">
                  Organization Phone <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={orgData.phone}
                  onChange={handleOrgChange}
                  placeholder="xxx-xxx-xxxx"
                  disabled={isSubmitting}
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>

              <div className="org-setup-field full-width">
                <label htmlFor="website">Website</label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={orgData.website}
                  onChange={handleOrgChange}
                  placeholder="https://www.company.com"
                  disabled={isSubmitting}
                />
                <span className="hint">Optional</span>
              </div>
            </div>
          </div>

          {/* First Store Location Section */}
          <div className="org-setup-section">
            <h3 className="org-setup-section-title">First Store Location</h3>
            <p className="org-setup-section-subtitle">
              Set up your first store. You can add more locations later from Settings.
            </p>

            <div className="org-setup-grid">
              <div className="org-setup-field full-width">
                <label htmlFor="storeName">
                  Store Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="storeName"
                  name="storeName"
                  value={locationData.storeName}
                  onChange={handleLocationChange}
                  placeholder="e.g., Downtown Location"
                  disabled={isSubmitting}
                />
                {errors.storeName && <span className="error-message">{errors.storeName}</span>}
              </div>

              <div className="org-setup-field full-width">
                <label htmlFor="addressLine1">
                  Street Address <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="addressLine1"
                  name="addressLine1"
                  value={locationData.addressLine1}
                  onChange={handleLocationChange}
                  placeholder="123 Main Street"
                  disabled={isSubmitting}
                />
                {errors.addressLine1 && <span className="error-message">{errors.addressLine1}</span>}
              </div>

              <div className="org-setup-field full-width">
                <label htmlFor="addressLine2">Address Line 2</label>
                <input
                  type="text"
                  id="addressLine2"
                  name="addressLine2"
                  value={locationData.addressLine2}
                  onChange={handleLocationChange}
                  placeholder="Suite, Unit, Building (optional)"
                  disabled={isSubmitting}
                />
              </div>

              <div className="org-setup-field">
                <label htmlFor="city">
                  City <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={locationData.city}
                  onChange={handleLocationChange}
                  placeholder="City"
                  disabled={isSubmitting}
                />
                {errors.city && <span className="error-message">{errors.city}</span>}
              </div>

              <div className="org-setup-field">
                <label htmlFor="state">
                  {stateLabel} <span className="required">*</span>
                </label>
                <select
                  id="state"
                  name="state"
                  value={locationData.state}
                  onChange={handleLocationChange}
                  disabled={isSubmitting}
                >
                  <option value="">Select {stateLabel}</option>
                  {stateProvinceOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {errors.state && <span className="error-message">{errors.state}</span>}
              </div>

              <div className="org-setup-field">
                <label htmlFor="postalCode">
                  {postalLabel} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={locationData.postalCode}
                  onChange={handleLocationChange}
                  placeholder={country === 'USA' ? '12345' : 'A1A 1A1'}
                  disabled={isSubmitting}
                />
                {errors.postalCode && <span className="error-message">{errors.postalCode}</span>}
              </div>

              <div className="org-setup-field">
                <label htmlFor="storePhoneNumber">
                  Store Phone <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="storePhoneNumber"
                  name="storePhoneNumber"
                  value={locationData.storePhoneNumber}
                  onChange={handleLocationChange}
                  placeholder="xxx-xxx-xxxx"
                  disabled={isSubmitting}
                />
                {errors.storePhoneNumber && <span className="error-message">{errors.storePhoneNumber}</span>}
              </div>

              <div className="org-setup-field">
                <label htmlFor="storeEmail">
                  Store Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="storeEmail"
                  name="storeEmail"
                  value={locationData.storeEmail}
                  onChange={handleLocationChange}
                  placeholder="store@company.com"
                  disabled={isSubmitting}
                />
                {errors.storeEmail && <span className="error-message">{errors.storeEmail}</span>}
              </div>

              <div className="org-setup-field full-width">
                <label htmlFor="storeWebsite">Store Website</label>
                <input
                  type="url"
                  id="storeWebsite"
                  name="storeWebsite"
                  value={locationData.storeWebsite}
                  onChange={handleLocationChange}
                  placeholder="https://store.company.com"
                  disabled={isSubmitting}
                />
                <span className="hint">Optional - if different from organization website</span>
              </div>
            </div>
          </div>

          <div className="submit-row">
            <FormButton className="Button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Organization & Continue'}
            </FormButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default OrganizationSetupForm;
